import { useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { Chip } from '@/components/commons/chip'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	AlertTriangleIcon,
	CheckIcon,
	Expand01Icon
} from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { Spinner } from '@/components/ui/spinner'
import { Tooltip } from '@/components/ui/tooltip'
import {
	OpsVettingCardBodyContainer,
	OpsVettingCardContainer,
	OpsVettingCardTitle,
	OpsVettingImgPlaceHolder,
	OpsVettingLabel,
	OpsVettingParagraph
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { VettingSectionStatusSelector } from '@/features/ops-vetting/components/vetting-section-status-selector'
import { ServiceVettingCriteriaEditOfferingPhotoButton } from '@/features/service-vetting/components/service-vetting-criteria-edit-offering-photo-button'
import { ServiceVettingCriteriaFlags } from '@/features/service-vetting/components/service-vetting-criteria-flags'
import { useAddServiceOfferingAnswerFlags } from '@/features/service-vetting/query/use-add-service-offering-answer-flags.ts'
import { useAddServiceOfferingFlags } from '@/features/service-vetting/query/use-add-service-offering-flags.ts'
import { useRemoveServiceOfferingAnswerFlags } from '@/features/service-vetting/query/use-remove-service-offering-answer-flags.ts'
import { useRemoveServiceOfferingFlags } from '@/features/service-vetting/query/use-remove-service-offering-flags.ts'
import { useRestoreMainPhotoOffering } from '@/features/service-vetting/query/use-restore-main-photo-offering.ts'
import { useUpdateAdminServiceOfferingStatus } from '@/features/service-vetting/query/use-update-service-offerings-status.ts'
import { useServiceVettingError } from '@/features/service-vetting/store/use-service-vetting-error.ts'
import type { RemappedOffering } from '@/features/service-vetting/types.ts'
import { formatCurrency } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

const OfferingDetailsSection = ({
	offering,
	isBackToDraft,
	hideAction,
	addOfferingQuestionFlags,
	removeOfferingQuestionFlags
}: {
	offering: RemappedOffering
	isBackToDraft: boolean
	hideAction?: boolean
	addOfferingQuestionFlags: ReturnType<
		typeof useAddServiceOfferingAnswerFlags
	>
	removeOfferingQuestionFlags: ReturnType<
		typeof useRemoveServiceOfferingAnswerFlags
	>
}) => {
	const { t } = useTranslation()

	return (
		<div className='flex w-full flex-col gap-2.5'>
			<div className='flex w-full flex-col gap-2'>
				<OpsVettingLabel>
					{t('vetting:criteria.service.offerings.offering_title')}
				</OpsVettingLabel>
				<OpsVettingParagraph>
					{offering.question_answer.title.answer}
				</OpsVettingParagraph>
				{isBackToDraft && (
					<ServiceVettingCriteriaFlags
						addFlag={(flag) =>
							addOfferingQuestionFlags.mutate({
								flags: [flag],
								itemId: offering.question_answer.title.id
							})
						}
						flags={offering.question_answer.title.flags}
						hideAction={hideAction}
						removeFlag={(flag) =>
							removeOfferingQuestionFlags.mutate({
								flags: [flag],
								itemId: offering.question_answer.title.id
							})
						}
						types={['general']}
					/>
				)}
			</div>
			<div className='flex w-full flex-col gap-2'>
				<OpsVettingLabel>
					{t('vetting:criteria.service.offerings.price')}
				</OpsVettingLabel>
				<OpsVettingParagraph>
					{offering.fixed_price
						? t('vetting:criteria.service.offerings.fixed_price', {
								price: formatCurrency(
									Number.parseFloat(
										`${offering.fixed_price}`
									),
									offering.question_answer.currency.answer
								)
							})
						: t(
								'vetting:criteria.service.offerings.price_per_guest',
								{
									price: formatCurrency(
										Number.parseFloat(
											offering.question_answer
												.price_per_guest.answer
										),
										offering.question_answer.currency.answer
									)
								}
							)}
				</OpsVettingParagraph>
				{isBackToDraft &&
					!!offering.question_answer.price_per_guest.answer && (
						<ServiceVettingCriteriaFlags
							addFlag={(flag) =>
								addOfferingQuestionFlags.mutate({
									flags: [flag],
									itemId: offering.question_answer
										.price_per_guest.id
								})
							}
							flags={
								offering.question_answer.price_per_guest.flags
							}
							hideAction={hideAction}
							removeFlag={(flag) =>
								removeOfferingQuestionFlags.mutate({
									flags: [flag],
									itemId: offering.question_answer
										.price_per_guest.id
								})
							}
							types={['general']}
						/>
					)}
			</div>
			{offering.question_answer.price_per_group && (
				<div className='flex w-full flex-col gap-2'>
					<OpsVettingLabel>
						{t('vetting:criteria.service.offerings.private_group')}
					</OpsVettingLabel>
					<div className='flex items-center gap-2'>
						<CheckIcon className='fill-success-600' size={16} />
						<OpsVettingParagraph>
							{t(
								'vetting:criteria.service.offerings.price_per_guest',
								{
									price: formatCurrency(
										Number.parseFloat(
											offering.question_answer
												.price_per_group.answer
										),
										offering.question_answer.currency.answer
									)
								}
							)}
						</OpsVettingParagraph>
					</div>
					{isBackToDraft && (
						<ServiceVettingCriteriaFlags
							addFlag={(flag) =>
								offering.question_answer.price_per_group &&
								addOfferingQuestionFlags.mutate({
									flags: [flag],
									itemId: offering.question_answer
										.price_per_group.id
								})
							}
							flags={
								offering.question_answer.price_per_group.flags
							}
							hideAction={hideAction}
							removeFlag={(flag) =>
								offering.question_answer.price_per_group &&
								removeOfferingQuestionFlags.mutate({
									flags: [flag],
									itemId: offering.question_answer
										.price_per_group.id
								})
							}
							types={['general']}
						/>
					)}
				</div>
			)}
			<div className='flex w-full flex-col gap-2'>
				<OpsVettingLabel>
					{t('vetting:criteria.service.offerings.duration')}
				</OpsVettingLabel>
				<OpsVettingParagraph>
					{offering.question_answer.duration.answer}
				</OpsVettingParagraph>
				{isBackToDraft && (
					<ServiceVettingCriteriaFlags
						addFlag={(flag) =>
							addOfferingQuestionFlags.mutate({
								flags: [flag],
								itemId: offering.question_answer.duration.id
							})
						}
						flags={offering.question_answer.duration.flags}
						hideAction={hideAction}
						removeFlag={(flag) =>
							removeOfferingQuestionFlags.mutate({
								flags: [flag],
								itemId: offering.question_answer.duration.id
							})
						}
						types={['general']}
					/>
				)}
			</div>
			<div className='flex w-full flex-col gap-2'>
				<OpsVettingLabel>
					{t('vetting:criteria.service.offerings.description')}
				</OpsVettingLabel>
				<OpsVettingParagraph>
					{offering.question_answer.description.answer}
				</OpsVettingParagraph>
				{isBackToDraft && (
					<ServiceVettingCriteriaFlags
						addFlag={(flag) =>
							addOfferingQuestionFlags.mutate({
								flags: [flag],
								itemId: offering.question_answer.description.id
							})
						}
						flags={offering.question_answer.description.flags}
						hideAction={hideAction}
						removeFlag={(flag) =>
							removeOfferingQuestionFlags.mutate({
								flags: [flag],
								itemId: offering.question_answer.description.id
							})
						}
						types={['general', 'description']}
					/>
				)}
			</div>
		</div>
	)
}

const OfferingPhotoSection = ({
	offering,
	isBackToDraft,
	isEditedImage,
	hideAction,
	galleryId,
	index,
	addOfferingQuestionFlags,
	removeOfferingQuestionFlags,
	restoreMainPhotoOffering,
	setShowGalleryModal
}: {
	offering: RemappedOffering
	isBackToDraft: boolean
	isEditedImage: boolean
	hideAction?: boolean
	galleryId: number
	index: number
	addOfferingQuestionFlags: ReturnType<
		typeof useAddServiceOfferingAnswerFlags
	>
	removeOfferingQuestionFlags: ReturnType<
		typeof useRemoveServiceOfferingAnswerFlags
	>
	restoreMainPhotoOffering: ReturnType<typeof useRestoreMainPhotoOffering>
	setShowGalleryModal: (show: boolean) => void
}) => {
	const { t } = useTranslation()

	return (
		<div className='flex w-full flex-col gap-2'>
			{offering.question_answer.main_photo?.answer ? (
				<>
					<GalleryImageItem
						bottomRightSlot={
							!hideAction && (
								<ButtonLegacy
									className='show-on-hover'
									onClick={() => setShowGalleryModal(true)}
									shape='square'
									size='sm'
									type='button'
									variant='secondary'
								>
									<Expand01Icon />
								</ButtonLegacy>
							)
						}
						src={offering.question_answer.main_photo.answer}
					/>
					{isBackToDraft && (
						<ServiceVettingCriteriaFlags
							addFlag={(flag) =>
								offering.question_answer.main_photo &&
								addOfferingQuestionFlags.mutate({
									flags: [flag],
									itemId: offering.question_answer.main_photo
										.id
								})
							}
							flags={offering.question_answer.main_photo.flags}
							hideAction={hideAction}
							removeFlag={(flag) =>
								offering.question_answer.main_photo &&
								removeOfferingQuestionFlags.mutate({
									flags: [flag],
									itemId: offering.question_answer.main_photo
										.id
								})
							}
							types={['gallery_image']}
						/>
					)}
				</>
			) : (
				<OpsVettingImgPlaceHolder />
			)}
			{offering.status === 'accepted' && !hideAction && (
				<div className='flex w-full justify-between'>
					<ServiceVettingCriteriaEditOfferingPhotoButton
						currentSelectedImageId={offering.image_id || undefined}
						galleryId={galleryId}
						offeringId={offering.id}
						offeringTitle={t(
							`vetting:criteria.service.offerings.offering_type.${offering.type}`,
							{ count: index + 1 }
						)}
						serviceId={offering.submission_id}
					/>
					{isEditedImage && (
						<ButtonLegacy
							disabled={restoreMainPhotoOffering.isPending}
							onClick={() => restoreMainPhotoOffering.mutate()}
							type='button'
							variant={'tertiary'}
						>
							{t(
								'vetting:criteria.service.offerings.restore_photo'
							)}
							{restoreMainPhotoOffering.isPending && (
								<Spinner size={20} />
							)}
						</ButtonLegacy>
					)}
				</div>
			)}
		</div>
	)
}

export const ServiceVettingCriteriaOfferingCard = ({
	offering,
	galleryId,
	index,
	hideAction
}: {
	offering: RemappedOffering
	index: number
	hideAction?: boolean
	galleryId: number
}) => {
	const { t } = useTranslation()

	const [showGalleryModal, setShowGalleryModal] = useState(false)

	const updateOfferingStatus = useUpdateAdminServiceOfferingStatus({
		serviceId: offering.submission_id,
		offeringId: offering.id
	})

	const restoreMainPhotoOffering = useRestoreMainPhotoOffering({
		offeringId: offering.id,
		serviceId: offering.submission_id
	})

	const errors = useServiceVettingError((state) => state.errors)

	const addOfferingQuestionFlags = useAddServiceOfferingAnswerFlags({
		serviceId: offering.submission_id,
		sectionId: offering.id
	})
	const removeOfferingQuestionFlags = useRemoveServiceOfferingAnswerFlags({
		serviceId: offering.submission_id,
		sectionId: offering.id
	})

	const addOfferingFlags = useAddServiceOfferingFlags({
		serviceId: offering.submission_id,
		itemId: offering.id
	})
	const removeOfferingFlags = useRemoveServiceOfferingFlags({
		serviceId: offering.submission_id,
		itemId: offering.id
	})

	const isBackToDraft = offering.status === 'back_to_draft'
	const isDeclined = offering.status === 'declined'
	const isEditedImage = offering.image_id !== offering.original_image_id

	useHotkeys('Esc', () => setShowGalleryModal(false), {
		enabled: showGalleryModal
	})
	return (
		<OpsVettingCardContainer direction='column' gap={2}>
			<div className='flex w-full items-center justify-between gap-2'>
				<div className='flex items-center gap-8'>
					<OpsVettingCardTitle>
						{t(
							`vetting:criteria.service.offerings.offering_type.${offering.type}`,
							{ count: index + 1 }
						)}
					</OpsVettingCardTitle>
					<Tooltip
						content={
							<div className='flex flex-col gap-1'>
								<h4 className='font-medium'>
									{t(
										'vetting:criteria.service.offerings.edited_photo_tooltip.title'
									)}
								</h4>{' '}
								<p className='text-neutral-300 text-xs'>
									{t(
										'vetting:criteria.service.offerings.edited_photo_tooltip.description'
									)}
								</p>
							</div>
						}
						maxWidth={'214px'}
						rootProps={{ open: isEditedImage ? undefined : false }}
						triggerProps={{ asChild: true }}
					>
						<div>
							<VettingSectionStatusSelector
								disabled={hideAction || isEditedImage}
								onChange={(status) =>
									updateOfferingStatus.mutate({ status })
								}
								value={offering.status}
							/>
						</div>
					</Tooltip>
				</div>
				{errors.itemErrors
					.filter((error) => error.itemId === offering.id)
					.map((error) => (
						<Chip
							isActive
							key={`${error.itemId}-${error.error}`}
							label={t(error.error)}
							startSlot={<AlertTriangleIcon />}
							variant={'danger'}
						/>
					))}
			</div>
			<OpsVettingCardBodyContainer>
				<OfferingDetailsSection
					addOfferingQuestionFlags={addOfferingQuestionFlags}
					hideAction={hideAction}
					isBackToDraft={isBackToDraft}
					offering={offering}
					removeOfferingQuestionFlags={removeOfferingQuestionFlags}
				/>
				<OfferingPhotoSection
					addOfferingQuestionFlags={addOfferingQuestionFlags}
					galleryId={galleryId}
					hideAction={hideAction}
					index={index}
					isBackToDraft={isBackToDraft}
					isEditedImage={isEditedImage}
					offering={offering}
					removeOfferingQuestionFlags={removeOfferingQuestionFlags}
					restoreMainPhotoOffering={restoreMainPhotoOffering}
					setShowGalleryModal={setShowGalleryModal}
				/>
			</OpsVettingCardBodyContainer>
			{isDeclined && (
				<>
					<Separator orientation='horizontal' />
					<ServiceVettingCriteriaFlags
						addFlag={(flag) =>
							addOfferingFlags.mutate({ flags: [flag] })
						}
						badgeVariant={'danger'}
						flags={offering.flags}
						hideAction={hideAction}
						removeFlag={(flag) =>
							removeOfferingFlags.mutate({ flags: [flag] })
						}
						types={['decline_offering']}
					/>
				</>
			)}
			{showGalleryModal && offering && (
				<GalleryModal
					currentImage={{
						id: offering.image_id || 0,
						name: ''
					}}
					headerSlot={
						<GalleryModalHeader
							currentImage={{
								id: offering.image_id || 0,
								name: ''
							}}
							onClose={() => setShowGalleryModal(false)}
						/>
					}
					onClose={() => setShowGalleryModal(false)}
					thumbnails={{
						l: offering.question_answer.main_photo?.answer || '',
						m: '',
						s: '',
						xs: ''
					}}
				/>
			)}
		</OpsVettingCardContainer>
	)
}
